import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getSeries, getEpisodesBySeries, createEpisode, getNextVideoUrl } from "../services/api";
import { Film, Hash, Link, Image, Save, ChevronDown, CheckCircle, XCircle, Gift, Loader2 } from 'lucide-react';

const LAST_SERIES_ID_KEY = "movieapp_last_episode_series_id";

const Movies = () => {
  const [seriesList, setSeriesList] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState("");
  const [newEpisode, setNewEpisode] = useState({
    title: "",
    episodeNumber: "",
    videoUrl: "",
  });
  const [thumbFile, setThumbFile] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // Bunny'dan taklif so'ralayotgan payt (nom/raqam/link hali yozilmagan) UI'da
  // yuklanish belgisini ko'rsatish va formani vaqtincha bloklash uchun
  const [isSuggesting, setIsSuggesting] = useState(false);
  // Admin dropdown'da serialni tez almashtirsa, eski serial uchun ketayotgan
  // (episode/video URL) so'rovlar keyinroq javob berib, joriy serialning
  // qiymatlarini eskirgan ma'lumot bilan ustidan yozib qo'ymasligi uchun kuzatiladi
  const currentSeriesIdRef = useRef(selectedSeriesId);
  useEffect(() => {
    currentSeriesIdRef.current = selectedSeriesId;
  }, [selectedSeriesId]);

  useEffect(() => {
    getSeries()
      .then(response => {
        const data = Array.isArray(response) ? response : response.series || response.movies || [];
        setSeriesList(data);

        // Oxirgi marta epizod yuklangan serialni avtomatik tanlaydi (mavjud bo'lsa)
        const lastSeriesId = localStorage.getItem(LAST_SERIES_ID_KEY);
        if (lastSeriesId && data.some((s) => String(s.id) === lastSeriesId)) {
          setSelectedSeriesId(lastSeriesId);
        }
      })
      .catch((e) => setError("Seriallarni olishda xato: " + e.message));
  }, []);

  const handleSeriesSelect = (seriesId) => {
    setSelectedSeriesId(seriesId);
    setError("");
    if (seriesId) {
      localStorage.setItem(LAST_SERIES_ID_KEY, seriesId);
    }
  };

  // Bunny'ga oldindan yuklab qo'yilgan, hali ishlatilmagan videoni topib, video URL, epizod
  // raqami va nomini shundan to'ldiradi. Mos video topilmasa (Collection tugagan yoki
  // sozlanmagan), hech narsa taklif qilinmaydi - noto'g'ri taxminiy qiymat berishdan ko'ra,
  // admin qo'lda kiritgani ma'qul.
  const suggestVideoUrl = (seriesId) => {
    getNextVideoUrl(seriesId).then(({ videoUrl, episodeNumber }) => {
      // Admin shu orada boshqa serialga o'tgan bo'lsa, bu eskirgan javobni e'tiborsiz qoldiramiz
      if (currentSeriesIdRef.current !== seriesId) return;
      setIsSuggesting(false);
      if (!videoUrl) return;
      const series = seriesList.find((s) => String(s.id) === String(seriesId));
      setNewEpisode((prev) => ({
        ...prev,
        videoUrl,
        ...(episodeNumber != null
          ? { episodeNumber: String(episodeNumber), title: series ? `${series.title} - ${episodeNumber}-qism` : prev.title }
          : {}),
      }));
    });
  };

  useEffect(() => {
    setEpisodes([]);
    if (!selectedSeriesId) return;
    const requestedSeriesId = selectedSeriesId;
    // Serial almashtirilganda avvalgi serialdan qolgan qiymatlar (ayniqsa video link)
    // darhol tozalanadi - aks holda yangi serialda taklif topilmasa, eski link ko'rinib qoladi
    setNewEpisode({ title: "", episodeNumber: "", videoUrl: "" });
    setIsSuggesting(true);
    getEpisodesBySeries(requestedSeriesId)
      .then((data) => {
        // Admin shu orada boshqa serialga o'tgan bo'lsa, bu eskirgan javobni e'tiborsiz qoldiramiz
        if (currentSeriesIdRef.current !== requestedSeriesId) return;
        setEpisodes(data);
        suggestVideoUrl(requestedSeriesId);
      })
      .catch((e) => {
        if (currentSeriesIdRef.current !== requestedSeriesId) return;
        setError("Episodlarni olishda xato: " + e.message);
        setIsSuggesting(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeriesId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!selectedSeriesId || !newEpisode.title || !newEpisode.episodeNumber || !newEpisode.videoUrl) {
      setError("Iltimos, barcha maydonlarni to‘ldiring.");
      return;
    }

    const formData = new FormData();
    formData.append("title", newEpisode.title);
    formData.append("episodeNumber", newEpisode.episodeNumber);
    formData.append("videoUrl", newEpisode.videoUrl);
    if (thumbFile) formData.append("image", thumbFile);

    try {
      const created = await createEpisode(selectedSeriesId, formData);
      const updatedEpisodes = [...episodes, created];
      setEpisodes(updatedEpisodes);
      setSuccessMessage(`✅ Yangi epizod (${newEpisode.title}) muvaffaqiyatli yaratildi!`);

      // Formani tozalash, keyingi epizod uchun nom/raqam/video URL avtomatik taklif qilish
      setNewEpisode({ title: "", episodeNumber: "", videoUrl: "" });
      setIsSuggesting(true);
      suggestVideoUrl(selectedSeriesId);
      setThumbFile(null);
    } catch (err) {
      console.error(err);
      setError("❌ Episod qo‘shishda xatolik: " + (err.response?.data?.message || err.message || 'Server xatosi'));
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbFile(file);
      setError(""); 
    }
  };

  return (
    // ✨ TUZATISH: lg:ml-64 uslubi olib tashlandi, chunki bu komponentning o'zi mobil ko'rinishda oq bo'sh joy qoldirishi mumkin
    // Asosiy kontent endi barcha ekranlarda to'g'ri joylashadi
    <div className="min-h-screen bg-[#0f111a] flex justify-center items-center p-4 sm:p-6 lg:p-8 text-white">
      
      {/* Kartaning kengligi va stili */}
      <div className="max-w-lg w-full bg-[#1c1e2c] p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-700/70">
        
        {/* Sarlavha */}
        <div className="mb-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-yellow-400">
                Yangi Epizod Qo‘shish 🎬
            </h1>
            <p className="text-gray-400 mt-2 text-sm">Serial uchun video ma'lumotlarini kiriting.</p>
        </div>
        
        {/* Xatolik / Muvaffaqiyat xabarlari */}
        {error && (
          <div className="flex items-center p-3 mb-6 bg-red-900/40 border border-red-600 rounded-lg text-red-300 text-sm shadow-lg animate-pulse">
            <XCircle className="w-5 h-5 mr-3"/>
            {error}
          </div>
        )}
        {successMessage && (
            <div className="flex items-center p-3 mb-6 bg-green-900/40 border border-green-600 rounded-lg text-green-300 text-sm shadow-lg">
                <CheckCircle className="w-5 h-5 mr-3"/>
                {successMessage}
            </div>
        )}

        <form onSubmit={handleCreate} className="space-y-6">
          
          {/* Series select */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Serial tanlang: <span className="text-gray-500 text-xs">(oxirgi tanlangan serial avtomatik saqlanadi)</span>
            </label>
            <div className="relative">
                <select
                className="w-full p-3 bg-[#0f111a] border border-gray-600 rounded-lg text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner cursor-pointer"
                value={selectedSeriesId}
                onChange={(e) => handleSeriesSelect(e.target.value)}
                required
                >
                <option value="" className='bg-[#1c1e2c]'>-- Serial tanlang --</option>
                {seriesList.map((s) => (
                    <option key={s.id} value={s.id} className='bg-[#1c1e2c]'>
                    {s.title}
                    </option>
                ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {isSuggesting && (
            <div className="flex items-center gap-2 text-sm text-blue-300 bg-blue-900/20 border border-blue-700/40 rounded-lg px-3 py-2 -my-2">
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
              Bunny'dan epizod ma'lumotlari qidirilmoqda...
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Epizod nomi:
            </label>
            <div className="relative">
                <Film className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                    type="text"
                    value={newEpisode.title}
                    onChange={(e) =>
                        setNewEpisode({ ...newEpisode, title: e.target.value })
                    }
                    className="w-full p-3 pl-10 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner placeholder-gray-500 disabled:opacity-50"
                    required
                    disabled={isSuggesting}
                    placeholder="Masalan: 1-Qism: Sarguzasht boshlanishi"
                />
            </div>
          </div>

          {/* Episode Number */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Epizod raqami:
            </label>
            <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                    type="number"
                    min="1"
                    value={newEpisode.episodeNumber}
                    onChange={(e) =>
                        setNewEpisode({ ...newEpisode, episodeNumber: e.target.value })
                    }
                    className="w-full p-3 pl-10 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner disabled:opacity-50"
                    required
                    disabled={isSuggesting}
                />
            </div>
          </div>

          {/* Fasl va bepul holati avtomatik hisoblanadi */}
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
            Fasl va bonus holati epizod raqamiga qarab avtomatik belgilanadi (seriali va fasllar sozlamalariga qarab).{" "}
            <RouterLink to="/seasons" className="text-yellow-400 hover:text-yellow-300">Fasllarni boshqarish →</RouterLink>
          </p>

          {/* Video Link */}
          <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">
              Video URL: <span className="text-gray-500 text-xs">(Bunny'ga oldindan yuklangan bo'lsa, avtomatik taklif qilinadi)</span>
              </label>
              <div className="relative">
                  <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                      type="text"
                      value={newEpisode.videoUrl}
                      onChange={(e) =>
                          setNewEpisode({ ...newEpisode, videoUrl: e.target.value })
                      }
                      className="w-full p-3 pl-10 bg-[#0f111a] border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-inner placeholder-gray-500 disabled:opacity-50"
                      required
                      disabled={isSuggesting}
                      placeholder="Video manzilini kiriting"
                  />
              </div>
          </div>

          {/* Thumbnail upload */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Epizod rasmi (Thumbnail): <span className="text-gray-500 text-xs">(ixtiyoriy — tanlamasangiz, Bunny'dagi videodan avtomatik olinadi)</span>
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">

              <label className={`cursor-pointer flex items-center space-x-2 py-2 px-4 rounded-lg shadow transition duration-200 text-sm font-medium min-w-[150px]
                  ${thumbFile ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}
              `}>
                <Image className="w-5 h-5" />
                <span>
                  {thumbFile ? "Tanlandi" : "Rasm tanlash (ixtiyoriy)"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>

              {thumbFile ? (
                <div className="flex items-center space-x-2">
                    <img
                        src={URL.createObjectURL(thumbFile)}
                        alt="Thumbnail Preview"
                        className="w-16 h-12 rounded object-cover border-2 border-gray-600 shadow-md"
                    />
                    <span className="text-xs text-gray-400 truncate max-w-[150px]">{thumbFile.name}</span>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Avtomatik: Bunny'dan olinadi</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSuggesting}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white font-semibold text-lg rounded-lg hover:bg-blue-700 transition duration-300 ease-in-out shadow-lg shadow-blue-500/50 transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <Save className="w-5 h-5" />
            <span>Epizodni Yaratish</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Movies;